'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUpIcon, CheckIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

import { SparkleIcon } from '@/components/shell-icons'
import { BorderBeam } from '@/components/ui/border-beam'
import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { useDashboardCommands } from '@/features/dashboard/hooks/use-dashboard-commands'
import {
  COMMON_PROMPT_ASSETS,
  parsePromptIntent,
} from '@/features/dashboard/lib/palette-prompt-intent'
import { HOME_EASE, HOME_REVEAL, HOME_SPRING } from '@/features/home/components/home-motion'
import { cn } from '@/lib/utils'

type AskMode = 'research' | 'trade'

const KNOWN_ASSETS: ReadonlySet<string> = new Set(COMMON_PROMPT_ASSETS)

// Prompt text is the assistant's / order prompt's input, so it stays in English like the
// palette's examples (palette-prompt-intent.ts) and the assistant's recommended prompts.
const RESEARCH_SUGGESTIONS = [
  "What's driving BTC's price today?",
  'Summarize the latest crypto market trends',
  'Explain funding rates like I am new to trading',
  'What is a liquidation price and how do I avoid it?',
] as const

const TRADE_SUGGESTIONS = [
  'buy 0.01 BTC at market',
  'sell 0.1 ETH limit at best ask',
  'buy 1 SOL twap over 1h',
  'dca $100 into BTC every day for 10 days',
] as const

const MAX_ROWS_PX = 120

interface HomeAskAiProps {
  /** Sends the live portfolio briefing to the assistant (built by the page from its snapshot). */
  onAskWhatHappened: () => void
  /** Sends the last 30 days' trading numbers for a coach-style review. */
  onReviewHabits?: () => void
}

// Focus inside one of these keeps its own keystrokes (a field, an open dialog or menu).
const KEEPS_KEYS =
  'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"], [role="combobox"]'
// Space and Enter activate a focused control; they don't start a message.
const ACTIVATES_ON_SPACE = 'button, a[href], [role="button"], [role="tab"], [role="switch"]'

/**
 * The AI, front and centre: one box that either asks the research assistant (opens it with the
 * question sent) or drafts a trade in plain words (fills the Order Form's prompt bar for review —
 * nothing is sent until the user confirms there). Typing a trade switches the mode on its own
 * until the user picks one; "What happened today?" sends the live portfolio snapshot along.
 * Anywhere on Home, starting to type writes into the box.
 */
export function HomeAskAi({ onAskWhatHappened, onReviewHabits }: HomeAskAiProps) {
  const t = useTranslations('trade.home.ai')
  const { startResearchPrompt, startTradePrompt } = useDashboardCommands()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [text, setText] = useState('')
  const [mode, setMode] = useState<AskMode>('research')
  const [modePicked, setModePicked] = useState(false)
  const [focused, setFocused] = useState(false)
  const [sent, setSent] = useState<AskMode | null>(null)

  const intent = useMemo(() => parsePromptIntent(text, KNOWN_ASSETS), [text])
  // Until the user picks a mode, a clear trade instruction ("buy 0.5 eth") flips to Trade and a
  // question flips back — the same read the ⌘K palette makes.
  const autoMode: AskMode | null = !modePicked && intent?.strong ? intent.primary : null
  const effectiveMode = autoMode ?? mode

  // Type anywhere on Home and the text lands in the box. Capture phase, so the keystroke is
  // taken before the app's single-key shortcuts (c, b, 1–9 …) read it as a command.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const input = inputRef.current
      if (!input || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
      // Keys dispatched on the document or window (not an element) have no closest().
      const target = event.target instanceof Element ? event.target : null
      if (target === input || target?.closest(KEEPS_KEYS)) return
      if (document.querySelector('[role="dialog"][data-state="open"]')) return
      // An IME (Korean, Chinese) composes the character itself: hand it the box and let it.
      if (event.isComposing || event.key === 'Process') {
        input.focus()
        return
      }
      if (event.key.length !== 1) return
      if (event.key === ' ' && target?.closest(ACTIVATES_ON_SPACE)) return
      event.preventDefault()
      event.stopPropagation()
      setText((previous) => previous + event.key)
      input.focus()
      requestAnimationFrame(() => {
        const end = input.value.length
        input.setSelectionRange(end, end)
      })
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [])

  useEffect(() => {
    if (!sent) return
    const timer = setTimeout(() => setSent(null), 1800)
    return () => clearTimeout(timer)
  }, [sent])

  // Grow with the text up to a few lines, then scroll.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_ROWS_PX)}px`
  }, [text])

  const submit = (value: string, as: AskMode = effectiveMode) => {
    const prompt = value.trim()
    if (!prompt) return
    if (as === 'trade') startTradePrompt(intent?.text ?? prompt)
    else startResearchPrompt(prompt)
    setSent(as)
    setText('')
    setModePicked(false)
  }

  const askWhatHappened = () => {
    onAskWhatHappened()
    setSent('research')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submit(text)
    }
    if (event.key === 'Escape') inputRef.current?.blur()
  }

  const suggestions = effectiveMode === 'trade' ? TRADE_SUGGESTIONS : RESEARCH_SUGGESTIONS

  return (
    // The reveal moves the beam's wrapper, so the glow and the box it rides arrive together.
    <motion.div variants={HOME_REVEAL}>
      {/* The beam circles the box all the time, at the Libraries.dev demo's full strength. */}
      <BorderBeam>
        <section
          className={cn(
            'bg-card relative isolate overflow-hidden rounded-(--widget-radius) border transition-[border-color] duration-200',
            focused ? 'border-foreground/25' : 'border-foreground/10'
          )}
        >
          {/* Accent wash that wakes up while the box has focus. */}
          <motion.span
            aria-hidden="true"
            className="from-shell-accent/15 pointer-events-none absolute inset-x-0 -top-24 -z-10 h-48 bg-radial-[ellipse_at_top] to-transparent to-70%"
            initial={false}
            animate={{ opacity: focused ? 1 : 0.35, scaleX: focused ? 1 : 0.8 }}
            transition={{ duration: 0.5, ease: HOME_EASE }}
          />
          <div className="flex items-center gap-3 px-4 pt-3">
            <SegmentedControl
              layoutId="home-ai-mode"
              size="xs"
              value={effectiveMode}
              onChange={(next) => {
                setMode(next)
                setModePicked(true)
                inputRef.current?.focus()
              }}
              options={[
                { value: 'research', label: t('modes.research') },
                { value: 'trade', label: t('modes.trade') },
              ]}
              className="shrink-0"
            />
          </div>

          <div className="flex items-end gap-3 px-4 pt-2">
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={t(
                effectiveMode === 'trade' ? 'placeholderTrade' : 'placeholderResearch'
              )}
              aria-label={t('title')}
              className="text-foreground placeholder:text-foreground/35 min-h-10 flex-1 resize-none bg-transparent py-2 text-lg leading-6 tracking-tight outline-none md:text-xl"
            />
            <Button
              size="sm"
              shape="pill"
              aria-label={t('send')}
              disabled={!text.trim()}
              onClick={() => submit(text)}
              className="mb-1 size-8 shrink-0 p-0"
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={sent ? 'sent' : 'idle'}
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -12, opacity: 0 }}
                  transition={{ duration: 0.18, ease: HOME_EASE }}
                  className="flex"
                >
                  {sent ? <CheckIcon className="size-4" /> : <ArrowUpIcon className="size-4" />}
                </motion.span>
              </AnimatePresence>
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 px-4 pt-3 pb-4">
            {effectiveMode === 'research' && (
              <motion.button
                type="button"
                onClick={askWhatHappened}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                transition={HOME_SPRING}
                className="bg-shell-accent/10 text-shell-accent hover:bg-shell-accent/15 flex h-7 cursor-pointer items-center gap-1.5 rounded-full px-3 text-xs font-medium whitespace-nowrap transition-colors"
              >
                <SparkleIcon className="size-3" />
                {t('whatHappened')}
              </motion.button>
            )}
            {effectiveMode === 'research' && onReviewHabits && (
              <motion.button
                type="button"
                onClick={() => {
                  onReviewHabits()
                  setSent('research')
                }}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                transition={HOME_SPRING}
                className="bg-shell-accent/10 text-shell-accent hover:bg-shell-accent/15 flex h-7 cursor-pointer items-center gap-1.5 rounded-full px-3 text-xs font-medium whitespace-nowrap transition-colors"
              >
                <SparkleIcon className="size-3" />
                {t('reviewHabits')}
              </motion.button>
            )}
            <AnimatePresence mode="popLayout" initial={false}>
              {suggestions.map((suggestion, index) => (
                <motion.button
                  key={suggestion}
                  type="button"
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: index * 0.03, duration: 0.22 },
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.1 } }}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    // Research questions go straight out; a trade is put in the box to edit first.
                    if (effectiveMode === 'research') submit(suggestion, 'research')
                    else {
                      setText(suggestion)
                      setMode('trade')
                      setModePicked(true)
                      inputRef.current?.focus()
                    }
                  }}
                  className="bg-foreground/5 text-foreground/70 hover:bg-foreground/10 hover:text-foreground flex h-7 cursor-pointer items-center rounded-full px-3 text-xs whitespace-nowrap transition-colors"
                >
                  {suggestion}
                </motion.button>
              ))}
            </AnimatePresence>
          </div>
        </section>
      </BorderBeam>
    </motion.div>
  )
}
